<template>
  <div
    v-if="visible"
    ref="overlayRef"
    class="iwb-overlay"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
    @click.self="close"
    @keydown.esc.stop.prevent="close"
    @keydown.tab="onTabKey"
  >
    <div
      ref="modalRef"
      class="iwb-modal"
    >
      <header class="iwb-head">
        <div class="iwb-head__title">
          <span class="iwb-head__icon"><i class="fal fa-store" /></span>
          <div>
            <h3>{{ t('templateBuilder.panels.widgets.marketplaceModal.title') }}</h3>
            <p>{{ t('templateBuilder.panels.widgets.marketplaceModal.subtitle', { count: totalWidgets }) }}</p>
          </div>
        </div>
        <button class="iwb-close" type="button" :aria-label="t('templateBuilder.modals.cancel')" @click="close">
          <i class="far fa-times" />
        </button>
      </header>

      <div class="iwb-toolbar">
        <div class="iwb-search">
          <i class="far fa-search" />
          <input
            v-model="search"
            type="text"
            :placeholder="t('templateBuilder.panels.widgets.marketplaceModal.searchPlaceholder')"
          />
          <button v-if="search" class="iwb-search__clear" type="button" @click="search = ''">
            <i class="far fa-times" />
          </button>
        </div>
        <span class="iwb-count">
          {{ visibleWidgets.length }} {{ t('templateBuilder.panels.widgets.marketplaceModal.widgetsLabel') }}
        </span>
      </div>

      <div class="iwb-body">
        <aside class="iwb-rail">
          <button
            class="iwb-rail__item"
            :class="{ 'is-active': selectedSlug === 'all' }"
            type="button"
            @click="selectedSlug = 'all'"
          >
            <span class="iwb-rail__icon iwb-rail__icon--all"><i class="far fa-th-large" /></span>
            <span class="iwb-rail__name">{{ t('templateBuilder.panels.widgets.marketplaceModal.allIntegrations') }}</span>
            <span class="iwb-rail__count">{{ totalWidgets }}</span>
          </button>
          <button
            v-for="intg in railIntegrations"
            :key="intg.slug"
            class="iwb-rail__item"
            :class="{ 'is-active': selectedSlug === intg.slug, 'is-locked': !intg.installed }"
            type="button"
            @click="selectedSlug = intg.slug"
          >
            <span class="iwb-rail__icon">
              <img v-if="intg.icon" :src="intg.icon" alt="" />
              <span v-else>{{ intg.initial }}</span>
            </span>
            <span class="iwb-rail__name">
              {{ intg.name }}
              <small v-if="!intg.installed">{{ t('templateBuilder.panels.widgets.marketplaceModal.notInstalledShort') }}</small>
            </span>
            <span class="iwb-rail__count">{{ intg.count }}</span>
          </button>
        </aside>

        <main class="iwb-main">
          <div class="iwb-grid">
            <div
              v-for="card in visibleWidgets"
              :key="card.id"
              class="iwb-card"
              :class="{ 'iwb-card--locked': !card.installed }"
              :title="card.installed ? t('templateBuilder.panels.widgets.marketplaceModal.addCta') : ''"
              @click="card.installed && $emit('add', { slug: card.slug, key: card.key })"
            >
              <div class="iwb-card__preview" :style="card.preview ? { backgroundImage: `url(${card.preview})` } : undefined">
                <img v-if="card.icon && !card.preview" :src="card.icon" class="iwb-card__preview-icon" alt="" />
                <i v-else-if="!card.preview" class="fal fa-puzzle-piece" />
                <span v-if="card.installed" class="iwb-card__grab"><i class="far fa-plus" /></span>
              </div>
              <div class="iwb-card__body">
                <div class="iwb-card__name">{{ card.name }}</div>
                <div class="iwb-card__intg">
                  <img v-if="card.intgIcon" :src="card.intgIcon" alt="" />
                  <span>{{ card.intgName }}</span>
                </div>
                <div v-if="card.description" class="iwb-card__desc">{{ card.description }}</div>
              </div>
              <div class="iwb-card__foot">
                <button
                  v-if="card.installed"
                  class="iwb-add"
                  type="button"
                  @click.stop="$emit('add', { slug: card.slug, key: card.key })"
                >
                  <i class="far fa-plus" /> {{ t('templateBuilder.panels.widgets.marketplaceModal.addCta') }}
                </button>
                <button
                  v-else
                  class="iwb-install"
                  :class="{ 'iwb-install--soon': !marketplaceEnabled }"
                  type="button"
                  :disabled="!marketplaceEnabled"
                  @click.stop="marketplaceEnabled && $emit('install', card.entry)"
                >
                  <template v-if="marketplaceEnabled">
                    <i class="far fa-download" /> {{ t('templateBuilder.panels.widgets.appInstallCta') }}
                  </template>
                  <template v-else>
                    <i class="far fa-clock" /> {{ t('integrations.comingSoon.badge') }}
                  </template>
                </button>
              </div>
            </div>
          </div>

          <div v-if="visibleWidgets.length === 0" class="iwb-empty">
            <i class="fal fa-box-open" />
            <p>{{ t('templateBuilder.panels.widgets.marketplaceModal.noResults') }}</p>
          </div>
        </main>
      </div>

      <footer class="iwb-foot">
        <span class="iwb-foot__hint">
          <i class="far fa-hand-pointer" />
          {{ t('templateBuilder.panels.widgets.marketplaceModal.footerHint') }}
        </span>
        <a
          v-if="marketplaceEnabled"
          href="#"
          class="iwb-foot__link"
          @click.prevent="$emit('open-marketplace')"
        >
          {{ t('templateBuilder.panels.widgets.marketplaceModal.exploreFull') }}
          <i class="far fa-arrow-right" />
        </a>
        <span
          v-else
          class="iwb-foot__soon"
        >
          <i class="far fa-clock" />
          {{ t('integrations.comingSoon.subtitle') }}
        </span>
      </footer>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, computed, ref, watch, nextTick, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { pickLocalizedText } from '@/utils/localized-text';
import type { WidgetCatalogEntry, WidgetLocalizedText } from '@/types/integrations';

interface WidgetCard {
  id: string;
  slug: string;
  key: string;
  name: string;
  description: string;
  preview: string;
  icon: string;
  intgName: string;
  intgIcon: string;
  installed: boolean;
  entry: WidgetCatalogEntry;
}

export default defineComponent({
  name: 'IntegrationWidgetsBrowserModal',

  props: {
    visible: { type: Boolean, default: false },
    catalog: { type: Array as PropType<WidgetCatalogEntry[]>, default: () => [] },
    locale: { type: String, default: 'fr' },
    // Gating marketplace ShopiMind : quand `false`, les CTA « installer » et le
    // lien « explorer la marketplace » deviennent « Bientôt disponible » (le
    // parent — WidgetsPanel — relaie l'état depuis le point de vérité routeur).
    marketplaceEnabled: { type: Boolean, default: false },
  },

  emits: ['update:visible', 'add', 'install', 'open-marketplace'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const selectedSlug = ref<string>('all');
    const search = ref('');

    // G15 — accessibilité modale : refs pour le focus initial + focus-trap.
    const overlayRef = ref<HTMLElement | null>(null);
    const modalRef = ref<HTMLElement | null>(null);

    const focusableSelector = [
      'a[href]', 'button:not([disabled])', 'input:not([disabled])',
      'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    const focusableEls = (): HTMLElement[] => {
      if (!modalRef.value) return [];
      return Array.from(modalRef.value.querySelectorAll<HTMLElement>(focusableSelector))
        .filter((el) => el.offsetParent !== null);
    };

    // Focus-trap : Tab depuis le dernier élément → premier, Shift+Tab depuis le
    // premier → dernier ; le focus ne peut pas sortir de la modale.
    const onTabKey = (e: KeyboardEvent) => {
      const els = focusableEls();
      if (els.length === 0) return;
      const first = els[0];
      const last = els[els.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || active === overlayRef.value)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    // Reset filters whenever the modal (re)opens + focus initial (a11y).
    watch(() => props.visible, (open) => {
      if (open) {
        selectedSlug.value = 'all';
        search.value = '';
        nextTick(() => {
          const els = focusableEls();
          (els[0] ?? overlayRef.value)?.focus();
        });
      }
    });

    const localize = (text?: WidgetLocalizedText): string =>
      pickLocalizedText(text as any, String(props.locale || 'fr'));

    // Partner integrations only. First-party (kind: 'shopimind') widgets are
    // surfaced in the editor's NATIVE widgets list (not here) — this in-editor
    // marketplace is reserved for partner apps.
    const integrationEntries = computed<WidgetCatalogEntry[]>(
      () => (props.catalog || []).filter((e) => e.kind === 'partner'),
    );

    const totalWidgets = computed(() => integrationEntries.value.reduce(
      (acc, e) => acc + (e.widgets?.length || 0), 0,
    ));

    const railIntegrations = computed(() => integrationEntries.value.map((e) => ({
      slug: e.integration_slug,
      name: e.integration_name,
      icon: e.integration_icon_url || '',
      initial: (e.integration_name || '?').charAt(0).toUpperCase(),
      installed: e.shop_integration_status === 'active',
      count: e.widgets?.length || 0,
    })));

    const visibleWidgets = computed<WidgetCard[]>(() => {
      const q = search.value.trim().toLowerCase();
      const cards: WidgetCard[] = [];
      integrationEntries.value.forEach((e) => {
        if (selectedSlug.value !== 'all' && e.integration_slug !== selectedSlug.value) return;
        (e.widgets || []).forEach((w) => {
          const name = localize(w.name) || w.key;
          const description = localize(w.description);
          if (q && !`${name} ${description} ${e.integration_name}`.toLowerCase().includes(q)) return;
          cards.push({
            id: `${e.integration_slug}__${w.key}`,
            slug: e.integration_slug,
            key: w.key,
            name,
            description,
            preview: w.preview_image_url || '',
            icon: w.icon_url || e.integration_icon_url || '',
            intgName: e.integration_name,
            intgIcon: e.integration_icon_url || '',
            installed: e.shop_integration_status === 'active',
            entry: e,
          });
        });
      });
      return cards;
    });

    const close = () => emit('update:visible', false);

    return {
      t,
      selectedSlug,
      search,
      totalWidgets,
      railIntegrations,
      visibleWidgets,
      close,
      overlayRef,
      modalRef,
      onTabKey,
    };
  },
});
</script>

<style lang="scss" scoped>
.iwb-overlay {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(3px);
}

.iwb-modal {
  display: flex;
  flex-direction: column;
  width: min(1000px, 94vw);
  height: min(800px, 88vh);
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.28);
  overflow: hidden;
}

.iwb-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid #eef0f5;

  &__title { display: flex; align-items: center; gap: 14px; }
  &__icon {
    width: 42px; height: 42px; border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    background: linear-gradient(135deg, #eef1ff, #f3ecfe);
    color: #6366f1; font-size: 19px;
  }
  h3 { margin: 0; font-size: 17px; font-weight: 700; color: #1f2430; }
  p { margin: 2px 0 0; font-size: 12.5px; color: #8a90a2; }
}

.iwb-close {
  width: 34px; height: 34px; border-radius: 9px;
  border: none; background: #f3f4f8; color: #6b7280; cursor: pointer;
  transition: all 0.15s ease;
  &:hover { background: #e9ebf1; color: #1f2430; }
}

.iwb-toolbar {
  display: flex; align-items: center; gap: 14px;
  padding: 12px 22px; border-bottom: 1px solid #f1f2f6;
}
.iwb-search {
  position: relative; flex: 1; display: flex; align-items: center;
  > i { position: absolute; left: 13px; color: #9aa0b0; font-size: 13px; }
  input {
    width: 100%; height: 38px; padding: 0 34px; border-radius: 10px;
    border: 1px solid #e3e6ee; background: #fafbfd; font-size: 13.5px; color: #1f2430;
    transition: all 0.15s ease;
    &:focus { outline: none; border-color: #6366f1; background: #fff; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12); }
  }
  &__clear {
    position: absolute; right: 9px; width: 22px; height: 22px; border: none;
    background: #eceef3; color: #6b7280; border-radius: 6px; cursor: pointer; font-size: 11px;
    &:hover { background: #e0e2ea; }
  }
}
.iwb-count { font-size: 12px; color: #9aa0b0; white-space: nowrap; }

.iwb-body { flex: 1; display: flex; min-height: 0; }

.iwb-rail {
  width: 230px; flex: none; padding: 12px; overflow-y: auto;
  border-right: 1px solid #f1f2f6; background: #fcfcfe;

  &__item {
    width: 100%; display: flex; align-items: center; gap: 10px;
    padding: 8px 10px; border: none; background: transparent; border-radius: 10px;
    cursor: pointer; text-align: left; margin-bottom: 2px; transition: background 0.12s ease;
    &:hover { background: #f1f2f8; }
    &.is-active { background: #eef1ff; }
    &.is-active .iwb-rail__name { color: #4f46e5; font-weight: 650; }
    &.is-locked { opacity: 0.72; }
  }
  &__icon {
    width: 30px; height: 30px; flex: none; border-radius: 8px; overflow: hidden;
    display: flex; align-items: center; justify-content: center;
    background: #eef0f6; color: #6b7280; font-size: 12px; font-weight: 700;
    img { width: 100%; height: 100%; object-fit: cover; }
    &--all { background: #eef1ff; color: #6366f1; }
  }
  &__name {
    flex: 1; min-width: 0; font-size: 13px; color: #353a48; line-height: 1.2;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    small { display: block; font-size: 10px; color: #b0b4c0; font-weight: 500; }
  }
  &__count {
    flex: none; font-size: 11px; font-weight: 700; color: #8a90a2;
    background: #eef0f6; border-radius: 99px; padding: 1px 8px;
  }
}

.iwb-main { flex: 1; position: relative; overflow-y: auto; padding: 18px; }

.iwb-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(196px, 1fr)); gap: 14px;
}

.iwb-card {
  display: flex; flex-direction: column; border: 1px solid #e9ebf1; border-radius: 12px;
  background: #fff; overflow: hidden; transition: all 0.16s ease;

  &:not(.iwb-card--locked) { cursor: pointer; }
  &:not(.iwb-card--locked):hover {
    border-color: rgba(99, 102, 241, 0.45);
    box-shadow: 0 8px 22px rgba(79, 70, 229, 0.12);
    transform: translateY(-2px);
  }
  &:not(.iwb-card--locked):active { transform: scale(0.985); }

  &__preview {
    position: relative; height: 96px; background: linear-gradient(135deg, #f5f6fb, #eef1ff);
    background-size: cover; background-position: center;
    display: flex; align-items: center; justify-content: center;
    color: #c2c7d6; font-size: 28px;
    /* G15 — n'affiche/peint les previews hors écran que lorsqu'elles approchent
       du viewport de la grille (grande liste de widgets = moins de layout/paint). */
    content-visibility: auto;
    contain-intrinsic-size: 96px;
  }
  &__preview-icon { max-width: 46px; max-height: 46px; object-fit: contain; }
  &__grab {
    position: absolute; top: 8px; right: 8px; width: 24px; height: 24px; border-radius: 7px;
    display: flex; align-items: center; justify-content: center;
    background: rgba(255, 255, 255, 0.92); color: #6366f1; font-size: 11px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12); opacity: 0; transition: opacity 0.15s ease;
  }
  &:hover &__grab { opacity: 1; }

  &__body { padding: 10px 12px 6px; flex: 1; }
  &__name {
    font-size: 13px; font-weight: 650; color: #1f2430; line-height: 1.25;
    overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical;
  }
  &__intg {
    display: flex; align-items: center; gap: 5px; margin-top: 3px; font-size: 11px; color: #8a90a2;
    img { width: 13px; height: 13px; border-radius: 3px; object-fit: cover; }
    span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  }
  &__desc {
    margin-top: 6px; font-size: 11.5px; color: #9aa0b0; line-height: 1.35;
    overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  }
  &__foot { padding: 6px 12px 11px; }

  &--locked { background: #fbfbfd; }
  &--locked .iwb-card__preview { filter: grayscale(0.5); opacity: 0.7; }
}

.iwb-pill {
  display: inline-flex; align-items: center; gap: 4px; font-size: 10.5px; font-weight: 700;
  padding: 2px 9px; border-radius: 99px;
  &--ok { background: rgba(148, 200, 64, 0.14); color: #6a9a20; }
}
.iwb-install {
  display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 650;
  padding: 4px 10px; border-radius: 8px; border: 1px solid #d9dcea; background: #fff; color: #4f46e5; cursor: pointer;
  transition: all 0.14s ease;
  &:hover { background: #eef1ff; border-color: #c3c8ec; }

  &--soon {
    color: #8a90a2; background: #f3f4f8; border-color: #e3e6ee; cursor: default;
    &:hover { background: #f3f4f8; border-color: #e3e6ee; }
  }
}
.iwb-add {
  display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px; font-weight: 650;
  padding: 5px 12px; border-radius: 8px; border: none;
  background: #4f46e5; color: #fff; cursor: pointer; transition: all 0.14s ease;
  &:hover { background: #4338ca; }
}

.iwb-empty {
  position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center;
  color: #b8bdca; gap: 10px;
  i { font-size: 42px; }
  p { margin: 0; font-size: 13.5px; }
}

.iwb-foot {
  display: flex; align-items: center; justify-content: space-between; gap: 14px;
  padding: 12px 22px; border-top: 1px solid #eef0f5; background: #fcfcfe;
  &__hint { font-size: 12px; color: #9aa0b0; display: inline-flex; align-items: center; gap: 7px; }
  &__link {
    font-size: 12.5px; font-weight: 650; color: #4f46e5; text-decoration: none;
    display: inline-flex; align-items: center; gap: 6px;
    &:hover { text-decoration: underline; }
  }
  &__soon {
    font-size: 12px; font-weight: 600; color: #8a90a2;
    display: inline-flex; align-items: center; gap: 6px;
  }
}

</style>
